import { CalculationHelpers } from '../../_common/client/calculation-helpers';
import { trackEvent } from '@otto-ec/nexus-registry/pdp/tracking';
import { InstallmentConfig } from '../../_common/types/installment-plan-calculator';
import { getRetailPrice } from '../../_common/client/installment-plan';
import { calcInstallmentRate } from '../../_common/server/installment-plan';

const SELECTOR_SHEET = '.js_pdp_installment-plan-calculator__sheet';
const SELECTOR_DURATION_SELECTION_INPUT_LIST = `${SELECTOR_SHEET}__duration-selection`;
const SELECTOR_DURATION_SELECTION_INPUT_LIST_ITEM = `${SELECTOR_SHEET}__duration-value`;
const SELECTOR_INSTALLMENT = `${SELECTOR_SHEET}__installment`;

export class InstallmentPlanCalculatorSheet {
    private calculatorElement: HTMLElement = null as unknown as HTMLElement;
    private price?: number;
    private isInitialized = false;

    public setEffectivePrice(price: number): void {
        if (!Number.isFinite(price) || price < 0) {
            return;
        }

        this.price = price;
        if (this.isInitialized && this.calculatorElement.isConnected) {
            this.updateSelectedRate();
        }
    }

    public onSheetOpen(event: Event) {
        if (event && event.target instanceof HTMLElement) {
            const calculatorElement = InstallmentPlanCalculatorSheet.calculatorContent(event.target);
            if (calculatorElement) {
                this.calculatorElement = calculatorElement;
                this.initialize();
            }
        }
    }

    private initialize() {
        if (this.isInitialized) {
            return;
        }
        this.isInitialized = true;
        if (this.price === undefined) {
            const retailPrice = getRetailPrice(this.calculatorElement);
            if (!retailPrice) {
                throw new Error('no retail price found on data attribute');
            }
            this.price = retailPrice;
        }

        const defaultDuration = this.extractDefaultDurationFromHtml();
        const defaultDurationInput = this.getDefaultDurationInputElement(defaultDuration);
        this.checkDefaultDurationInput(defaultDurationInput);
        this.triggerCalculation(this.readInstallmentConfig(defaultDurationInput));
        trackEvent({
            product_InstallmentCalculator: ['open'],
            product_InstallmentCalculatorRate: [String(defaultDuration)],
        });

        this.registerDurationClickListeners();
    }

    private updateSelectedRate(): void {
        const selectedInput = this.calculatorElement.querySelector<HTMLInputElement>(
            `${SELECTOR_DURATION_SELECTION_INPUT_LIST_ITEM} input:checked`
        );
        if (!selectedInput) {
            throw new Error('could not find selected duration input');
        }

        this.triggerCalculation(this.readInstallmentConfig(selectedInput));
    }

    private extractDefaultDurationFromHtml(): number {
        const defaultDurationContainer = this.calculatorElement.querySelector(SELECTOR_DURATION_SELECTION_INPUT_LIST);
        if (!defaultDurationContainer) {
            throw new Error('duration selection input list does not exist in dom');
        }

        const defaultDuration = defaultDurationContainer.getAttribute('data-default-duration');
        if (!defaultDuration) {
            throw new Error('could not find default duration attribute on duration selection input list dom element');
        }
        return +defaultDuration;
    }

    private getDefaultDurationInputElement(defaultDuration: number): HTMLInputElement {
        const defaultDurationInputSelector = `${SELECTOR_DURATION_SELECTION_INPUT_LIST_ITEM} input[value="${defaultDuration}"]`;
        const defaultDurationInput = this.calculatorElement.querySelector<HTMLInputElement>(defaultDurationInputSelector);
        if (!defaultDurationInput) {
            throw new Error('could not find duration selection input for default duration');
        }
        return defaultDurationInput;
    }

    private checkDefaultDurationInput(defaultDurationInput: HTMLInputElement) {
        defaultDurationInput.checked = true;
    }

    private readInstallmentConfig(inputElement: HTMLInputElement): InstallmentConfig {
        const duration = Number(inputElement.value);
        const surcharge = Number(inputElement.dataset.surcharge);
        if (!Number.isFinite(duration) || inputElement.dataset.surcharge === undefined || !Number.isFinite(surcharge)) {
            throw new Error('could not read installment config from duration input element');
        }

        return { duration, surcharge };
    }

    private registerDurationClickListeners() {
        const durationSelectionInputs = Array.from(this.calculatorElement.querySelectorAll(SELECTOR_DURATION_SELECTION_INPUT_LIST_ITEM));
        for (const durationSelectionInput of durationSelectionInputs) {
            const inputElement = durationSelectionInput?.firstElementChild as HTMLInputElement;
            if (inputElement) {
                const installmentConfig = this.readInstallmentConfig(inputElement);
                inputElement.addEventListener('change', (event: Event) => {
                    event.stopPropagation();
                    this.handleDurationSelectionChange(installmentConfig);
                });
            }
        }
    }

    private handleDurationSelectionChange(installmentConfig: InstallmentConfig) {
        this.triggerCalculation(installmentConfig);
        trackEvent({
            product_InstallmentCalculator: ['changerate'],
            product_InstallmentCalculatorRate: [String(installmentConfig.duration)],
        });
    }

    private triggerCalculation(installmentConfig: InstallmentConfig) {
        const monthlyRate = this.calculateMonthlyRate(installmentConfig);
        const monthlyRateFormatted = this.formatPrice(monthlyRate);
        this.setPrice(monthlyRateFormatted);
    }

    private setPrice(initialPriceFormatted: string) {
        const priceDisplayElement = this.calculatorElement.querySelector(SELECTOR_INSTALLMENT);
        if (!priceDisplayElement) {
            throw new Error('price display element does not exist');
        }
        priceDisplayElement.innerHTML = initialPriceFormatted;
    }

    private formatPrice(price: number) {
        return `${CalculationHelpers.convertFloatToString(price)} €`;
    }

    private calculateMonthlyRate(installmentConfig: InstallmentConfig) {
        if (this.price === undefined) {
            throw new Error('no price could be evaluated for calculating installment rates');
        }
        return calcInstallmentRate(installmentConfig, this.price);
    }

    private static calculatorContent(htmlElement: HTMLElement) {
        return htmlElement.querySelector<HTMLElement>(SELECTOR_SHEET);
    }
}
